import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { format, toZonedTime } from 'date-fns-tz'

import { getAssetLink } from '../../../../helpers/get-asset-link'
import Icon from '../../../Icons'

const DLT_BTN_WIDTH = 85
const DELAY_TIME = 2000
const STEP_MS = 50

const NoteItem = ({ note, timezone, userData, onEdit = () => {}, onDelete = () => {} }) => {
  const { t } = useTranslation(['reservations', 'common'])
  const [animationTime, setAnimationTime] = useState(0)
  const [showAnimation, setShowAnimation] = useState(false)
  const isAdmin = userData.user.role === 'Administrator'
  const canEditAndDelete = isAdmin || (note.createdBy && userData.user.id === note.createdBy.id)
  const animationSize =
    animationTime === 0 ? 0 : Math.round((animationTime * DLT_BTN_WIDTH) / DELAY_TIME)

  const deleteNote = () => onDelete(note.id)

  useEffect(() => {
    let interval
    if (showAnimation) {
      interval = setInterval(() => {
        setAnimationTime((time) => {
          if (time + STEP_MS >= DELAY_TIME) {
            deleteNote()
            setShowAnimation(false)
            return 0
          }

          return time + STEP_MS
        })
      }, STEP_MS)
    } else {
      setAnimationTime(0)
    }

    return () => clearInterval(interval)
  }, [showAnimation])

  return (
    <div className="reservation-note-item">
      <div className="note-item-content">
        <div className="note-item-head">
          <span className="note-item-label">{note.title}</span>
          <button
            className="button button-transparent"
            onClick={() => onEdit(note)}
            disabled={!canEditAndDelete}
          >
            <Icon icon="pencil" />
            {t('common:actions.edit')}
          </button>
          <button
            className="button note-delete"
            disabled={!canEditAndDelete}
            onMouseDownCapture={() => setShowAnimation(true)}
            onMouseUpCapture={() => setShowAnimation(false)}
            onMouseLeave={() => setShowAnimation(false)}
          >
            <Icon icon="cross" />
            <div style={{ right: DLT_BTN_WIDTH - animationSize }} />
            {t('common:actions.delete')}
          </button>
        </div>
        <span className="note-item-description">{note.noteBody}</span>
      </div>
      <div className="note-item-footer">
        <div>
          <span className="note-item-label">{t('reservations:noteItem.createdBy')}</span>
          {note.createdBy ? (
            <>
              <img
                src={getAssetLink({ path: 'chitown-avatar/avatar', file: note.createdBy.avatar })}
                alt=""
              />
              <span>{`${note.createdBy.firstName} ${note.createdBy.lastName}`}</span>
            </>
          ) : (
            <span>{t('reservations:noteItem.deletedUser')}</span>
          )}
        </div>
        <div>
          <span className="note-item-label">{t('reservations:noteItem.date')}</span>
          <span>
            {format(toZonedTime(note.createdAt, timezone), 'MM.dd.yyyy', { timeZone: timezone })}
          </span>
        </div>
      </div>
    </div>
  )
}

NoteItem.propTypes = {
  note: PropTypes.object,
  timezone: PropTypes.string,
  userData: PropTypes.object,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
}

export default NoteItem
